import React, { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Trans, useTranslation } from 'react-i18next';
import styled, { css, keyframes } from 'styled-components';
import { colorsPalette } from '../../../../../../shared/ui/styles/colors-palette';
import { useUILogic } from '../../../../shared/providers/UILogicProvider/hooks';
import { CONFIRMATION_PLACEHOLDER } from '../../../../../infrastructure/types/features/auth/auth-types';
import UiKit from '../../../../../../shared/ui/ui-kit';
import { LoadingStatus } from '../../../../../../shared/infrastructure/types/common/common-types';

let IncomingConfirmation = function () {
  const { t } = useTranslation();
  const { authorizationUiModule } = useUILogic();
  const coreManager = authorizationUiModule.registerUIManager.coreManager;
  const manager = authorizationUiModule.registerUIManager.coreManager.incomingConfirmationManager;

  const isExpired = manager.isConfirmationExpired;
  const dictatedCode = manager.confirmationCode ?? CONFIRMATION_PLACEHOLDER;
  const incomingNumber = coreManager.incomingPhone ?? CONFIRMATION_PLACEHOLDER;
  const loading = coreManager.verificationPrepareStatus === LoadingStatus.LOADING;

  useEffect(() => {
    if (!manager.isWasRequest) {
      manager.setIsWasRequest(true);
      manager.requestVerification();
    }

    let interval: ReturnType<typeof setTimeout>;
    if (manager.registrationId && !isExpired) {
      interval = setInterval(() => {
        manager.checkCodeByID();
      }, 500);
    }
    return () => clearInterval(interval);
  }, [manager.registrationId, manager.isConfirmationExpired]);

  const handleRefresh = useCallback(() => {
    manager.requestVerification();
  }, [manager]);

  return (
    <IncomingBlock>
      <Instruction>
        <Trans
          i18nKey={'register.phone.confirm.incoming.instruction'}
          values={{ number: incomingNumber }}
          components={{
            linkBtn: <UiKit.Link href={`tel:${incomingNumber}`} />,
          }}
        />
      </Instruction>
      <DictatedCode>
        {isExpired ? (
          <UiKit.Button
            icon={<Reload $loading={loading} />}
            type="link"
            size="small"
            onClick={() => {
              if (!loading) {
                handleRefresh();
              }
            }}
          >
            {loading ? t('common.loading') : t('register.phone.confirm.timer.reload')}
          </UiKit.Button>
        ) : (
          dictatedCode
        )}
      </DictatedCode>
      <Timer>
        {isExpired
          ? t(`register.phone.confirm.incoming.expired`)
          : t(`register.phone.confirm.incoming.valid`, {
              timer: manager.confirmationTimer,
            })}
      </Timer>
    </IncomingBlock>
  );
};

const Reload = styled(UiKit.icons.Reload)<{ $loading?: boolean }>`
  ${({ $loading }) =>
    $loading &&
    css`
      animation: ${spin} 1s linear infinite;
    `}
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const IncomingBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const DictatedCode = styled.div`
  font-family: 'SkorozvonRoboto';
  font-weight: 500;
  font-size: 30px;
  line-height: 40px;
  color: ${colorsPalette.gray600};
  text-align: center;
`;

const Instruction = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: ${colorsPalette.gray600};
  text-align: center;
  white-space: pre-line;
`;

const Timer = styled.div`
  font-size: 12px;
  line-height: 20px;
  color: ${colorsPalette.gray600};
  text-align: center;
`;

IncomingConfirmation = observer(IncomingConfirmation);

export { IncomingConfirmation };
